import React from "react";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../images/shop_logo.png";
import { SHOP_NAME } from "../Variables.js";
import { useUserAuth } from "../context/UserAuthContext";

const ProductsByCategoryPDFButton = ({ products = [], productsPerCategory = {} }) => {
  const { user, userData } = useUserAuth();
  const shopName = SHOP_NAME;

  const formatDateTime = (date) =>
    date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(",", "");

  const formatAmount = (amount) =>
    `N${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const checkPageBreak = (doc, finalY, requiredHeight = 0, bottomMargin = 0) => {
    const pageHeight = doc.internal.pageSize.height;
    if (finalY + requiredHeight > pageHeight - bottomMargin) {
      doc.addPage();
      return 20; // Reset to top margin of new page
    }
    return finalY;
  };

  const generateCategoryReportPDF = () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString("en-GB").replace(/\//g, "_");

    // Adding logo - same positioning
    doc.addImage(Logo, "PNG", 14, 10, 15, 15);

    // Adding store name - same styling
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(shopName, 35, 15);

    // Adding document title - same styling
    doc.setFontSize(16);
    doc.setFont("normal");
    doc.text("Products by Category Report", 35, 23);

    // Adding generated by and timestamp - same styling
    doc.setFontSize(10);
    const fullName = userData?.firstName && userData?.lastName 
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";
    const generatedBy = `Generated by: ${fullName}`;
    const generatedAt = `Generated on: ${formatDateTime(new Date())}`;
    doc.text(generatedBy, 35, 28);
    doc.text(generatedAt, 35, 33);

    // Generating category tables - same layout as ProductsPDFGenerator
    let finalY = 40;
    const categoryEntries = Object.entries(productsPerCategory);
    
    categoryEntries.forEach(([category, stats], index) => {
      // Check space for heading (approx 10px)
      finalY = checkPageBreak(doc, finalY, 10);

      // Add category heading - same styling
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`${category}`, 14, finalY);
      finalY += 2;

      // Prepare products table data for this category
      const categoryProducts = products.filter(product => (product.category || "Uncategorized") === category);
      const tableData = categoryProducts.map((product, productIndex) => {
        const isActive = product.isActive;
        const quantity = product.quantity || 0;
        const costPrice = product.costPrice || product.price || 0;
        const sellingPrice = product.price || 0;
        const totalValue = quantity * sellingPrice;
        const totalCost = quantity * costPrice;
        const profit = totalValue - totalCost;
        
        return [
          (productIndex + 1).toString(),
          product.name || "Unknown Product" + (!isActive ? " (INACTIVE)" : ""),
          quantity.toString(),
          formatAmount(costPrice),
          formatAmount(sellingPrice),
          formatAmount(totalValue),
          formatAmount(profit),
          isActive ? "Active" : "Inactive"
        ];
      });

      // Generate products table with auto pagination - same parameters
      autoTable(doc, {
        startY: finalY,
        head: [["S/N", "Product Name", "Quantity", "Cost Price", "Selling Price", "Total Value", "Profit", "Status"]],
        body: tableData,
        theme: "striped", // Changed from "plain" to "striped" to match
        headStyles: { fillColor: [41, 128, 185] },
        bodyStyles: { textColor: [0, 0, 0] },
        margin: { left: 8, right: 14, bottom: 30 }, // Same margins
        styles: { fontSize: 8, cellPadding: 2 }, // Same font size and padding
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 45 },
          2: { cellWidth: 20 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 25 },
          7: { cellWidth: 20 }
        },
        didDrawPage: (data) => {
          finalY = data.cursor.y + 10; // Update finalY after table
        },
        willDrawCell: (data) => {
          // Ensure table doesn't overflow - same logic
          if (data.row.section === "body" && data.cursor.y > doc.internal.pageSize.height - 30) {
            data.cursor.y = doc.internal.pageSize.height - 30; // Stop at bottom margin
          }
          
          // Color inactive products differently - same logic
          if (data.column.index === 8 && data.cell.raw === "Inactive") {
            doc.setTextColor(255, 0, 0); // Red for inactive
          } else if (data.column.index === 7) {
            // Color profit/loss cells - same logic
            const profitValue = parseFloat(data.cell.raw.replace(/[^\d.-]/g, ""));
            if (profitValue < 0) {
              doc.setTextColor(255, 0, 0); // Red for loss
            } else if (profitValue > 0) {
              doc.setTextColor(0, 128, 0); // Green for profit
            }
          }
        }
      });

      // Check space for totals (approx 30px for 5 lines)
      finalY = checkPageBreak(doc, finalY, 30);

      // Add category totals - same formatting
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0); // Reset text color
      doc.text(`Total Products: ${stats.count}`, 14, finalY);
      doc.text(`Total Items: ${stats.totalItems.toLocaleString()}`, 14, finalY + 5);
      doc.text(`Total Cost Price: ${formatAmount(stats.totalCostPrice)}`, 14, finalY + 10);
      doc.text(`Total Selling Price: ${formatAmount(stats.totalSellingPrice)}`, 14, finalY + 15);
      
      // Color profit/loss text - same logic
      if (stats.totalProfit < 0) {
        doc.setTextColor(255, 0, 0); // Red for loss
      } else if (stats.totalProfit > 0) {
        doc.setTextColor(0, 128, 0); // Green for profit
      }
      doc.text(`Total Profit: ${formatAmount(stats.totalProfit)}`, 14, finalY + 20);
      doc.setTextColor(0, 0, 0); // Reset text color
      
      finalY += 30;

      // Add horizontal line if not the last category - same styling
      if (index < categoryEntries.length - 1) {
        finalY = checkPageBreak(doc, finalY, 10); // Check space for line (approx 10px)
        doc.setLineWidth(0.3);
        doc.setDrawColor(100, 100, 100); // Set line color to dark gray (RGB)
        doc.line(14, finalY, doc.internal.pageSize.width - 14, finalY);
        finalY += 10; // Space after line
      }
    });

    // Adding footer with page number - same positioning
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    // Saving the PDF - similar filename format
    doc.save(`products_by_category_${currentDate}.pdf`);
  };

  return (
    <Link to={``}
      className="row align-center space-between first-cover monthly-report-button"
      onClick={generateCategoryReportPDF}
      style={{ marginTop: "12px", cursor: 'pointer' }}
    >
      <span className="italics size2 font-gold bold">4.</span>
      <p className="second">Download All Products</p>
    </Link>
  );
};

export default ProductsByCategoryPDFButton;